import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import ForwardToEndIcon from '@src/svgs/ForwardToEndIcon';
import { FullScreenIconAlt } from '@src/svgs/FullScreenIcon';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import SpeakerIcon from '@src/svgs/SpeakerIcon';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { formatPlaybackTime } from '../formatPlaybackTime';
import { useChromeFontSize } from '../useChromeFontSize';
import { useCursorHover } from './sharedChrome';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.linkedinFeedPost;

/**
 * LinkedIn Feed Post overlay — paints just the in-video player chrome a
 * LinkedIn feed reader sees. Bare state shows nothing; toggling Controls
 * paints LinkedIn's play-controls bar at the video bottom; toggling Captions
 * (via the CC button inside the controls) paints a placeholder caption block
 * in the lower-third.
 *
 * Scaling: chrome is sized in `em` against a single base font-size that
 * scales with the video rect. The reference rect is **510×309**, where the
 * base font-size resolves to `10px`. At any other rect size, every chrome
 * dimension scales proportionally — so "40px controls bar at 510×309"
 * renders as "80px controls bar at 1020×618" automatically.
 *
 * Use the conversion `1em = 10px @ reference` when adding new chrome:
 * - `40px` height → `4em`
 * - `15px` height → `1.5em`
 * - `4px` padding → `0.4em`
 * etc.
 */
const REFERENCE_WIDTH = 510;
const REFERENCE_HEIGHT = 309;
const REFERENCE_FONT_PX = 10;
// Floor scale at 0.6 so chrome never collapses below readable size on tiny
// preview rects; ceil at 3 so it doesn't blow up if mounted full-window.
const SCALE_MIN = 0.6;
const SCALE_MAX = 3;

export const LinkedInFeedPostOverlay: React.FC<SafeZoneProps> = ({
  rect,
  aspectRatio,
  variants,
  onToggleVariant,
  playback,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_WIDTH,
    referenceHeight: REFERENCE_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_MIN,
    scaleMax: SCALE_MAX,
  });

  const showControls = !!variants.showPlayControls;
  const showCaptions = !!variants.showCaptions;

  // LinkedIn renders captions much larger on square + vertical feed posts
  // than on landscape — they fill the lower third the way TikTok-style
  // captions do. 9:16 is narrower than 1:1, so its caption is sized down to
  // half the square value to leave room for line wraps without crowding the
  // chrome. Anchor stays at the same bottom offset; only the box size (and
  // wrap behavior) change.
  const captionSize: 'landscape' | 'square' | 'vertical' =
    aspectRatio === '9:16'
      ? 'vertical'
      : aspectRatio === '1:1'
        ? 'square'
        : 'landscape';

  // Live playback mirroring. When the bridge is absent (e.g. previewing in a
  // surface without a connected renderer), fall back to inert defaults — the
  // chrome still renders, it just doesn't reflect or drive a video.
  const isPlaying = playback?.isPlaying ?? false;
  const duration = playback?.duration ?? 0;
  const isReady = playback?.isReady ?? false;

  // Drag-and-seek state for the controls bar's scrubber rail. Shared with
  // the LinkedIn Shorts overlay via this hook so seek behavior — drag
  // continuity, seek-time precedence, unmount cleanup — stays identical
  // across overlays.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);

  const togglePlayPause = () => {
    if (!playback || !isReady) return;
    if (isPlaying) playback.pause();
    else playback.play();
  };

  const onBackToStart = () => {
    if (!playback || !isReady) return;
    playback.setTime(0);
  };

  // ---- Volume popover state -----------------------------------------------
  // The Renderer doesn't expose a volume API yet, so the slider is visually
  // accurate but doesn't change the underlying audio. Lives as local state for
  // now; once the renderer gains volume support, swap to fields on
  // `SafeZonePlaybackBridge`.
  const [volume, setVolume] = useState(1);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const speakerBtnRef = useRef<HTMLButtonElement | null>(null);
  const volumePopoverRef = useRef<HTMLDivElement | null>(null);
  const volumeRailRef = useRef<HTMLDivElement | null>(null);

  // Close the popover when the user clicks elsewhere or presses Escape.
  useEffect(() => {
    if (!volumeOpen) return;
    const onDown = (ev: MouseEvent) => {
      const t = ev.target as Node | null;
      if (
        speakerBtnRef.current?.contains(t) ||
        volumePopoverRef.current?.contains(t)
      )
        return;
      setVolumeOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setVolumeOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [volumeOpen]);

  const onVolumeRailMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const setFromY = (clientY: number) => {
      const el = volumeRailRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.height <= 0) return;
      // Top of rail = 1 (max), bottom = 0 (mute).
      const pct = 1 - Math.max(0, Math.min(1, (clientY - r.top) / r.height));
      setVolume(pct);
    };
    setFromY(e.clientY);
    const onMove = (ev: MouseEvent) => setFromY(ev.clientY);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // The chrome stays `pointer-events: none` so clicks pass through to the
  // underlying renderer. CSS `:hover` therefore won't fire on the play
  // button — `useCursorHover` checks the cursor against its bounding rect
  // on every window mousemove and gives us a hover boolean.
  const playBtnRef = useRef<HTMLButtonElement | null>(null);
  const playHovered = useCursorHover(playBtnRef, showControls);

  return (
    <Frame
      data-testid="linkedin-feed-post-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      {/* Caption — always mounted, fades in/out via opacity */}
      <CaptionAnchor $visible={showCaptions}>
        <CaptionBox $size={captionSize}>
          <CaptionLines>{COPY.captionPlaceholder}</CaptionLines>
        </CaptionBox>
      </CaptionAnchor>

      {showControls && (
        <ControlsBar>
          <ControlsInner>
            <PlayBtn
              ref={playBtnRef}
              type="button"
              onClick={togglePlayPause}
              aria-label={isPlaying ? COPY.aria.pause : COPY.aria.play}
              aria-pressed={isPlaying}
              disabled={!playback || !isReady}
              $hovered={playHovered}
            >
              {isPlaying ? (
                <MuxPauseIcon width="1.6em" height="1.6em" />
              ) : (
                <MuxPlayIcon width="1.6em" height="1.6em" />
              )}
            </PlayBtn>

            <BackToStartBtn
              type="button"
              onClick={onBackToStart}
              aria-label={COPY.aria.backToStart}
              disabled={!playback || !isReady}
            >
              <BackToStartGlyph>
                <ForwardToEndIcon
                  width="1.4em"
                  height="1.4em"
                  stroke="#ffffff"
                />
              </BackToStartGlyph>
            </BackToStartBtn>

            <Scrubber
              ref={scrubberRef}
              role="slider"
              aria-label={COPY.aria.seek}
              aria-valuemin={0}
              aria-valuemax={Math.max(0, duration)}
              aria-valuenow={Math.max(0, displayTime)}
              onMouseDown={onScrubberMouseDown}
              $interactive={!!playback && isReady && duration > 0}
            >
              <ScrubberFill style={{ width: `${progressPct}%` }} />
            </Scrubber>

            <Time>{formatPlaybackTime(displayTime)}</Time>

            <SpeedPill>1x</SpeedPill>

            <CCButton
              type="button"
              $active={showCaptions}
              onClick={() => onToggleVariant?.('showCaptions')}
              aria-pressed={showCaptions}
              aria-label={COPY.aria.toggleCaptions}
            >
              CC
            </CCButton>

            <VolumeWrap>
              <VolumeBtn
                ref={speakerBtnRef}
                type="button"
                onClick={() => setVolumeOpen(o => !o)}
                aria-label={COPY.aria.mute}
                aria-expanded={volumeOpen}
                $muted={volume === 0}
              >
                <SpeakerIcon
                  width="2em"
                  height="2em"
                  fillColor="#ffffff"
                  strokeColor="#ffffff"
                />
              </VolumeBtn>
              {volumeOpen && (
                <VolumePopover
                  ref={volumePopoverRef}
                  role="dialog"
                  aria-label={COPY.aria.volumeSlider}
                >
                  <VolumeRail
                    ref={volumeRailRef}
                    role="slider"
                    aria-orientation="vertical"
                    aria-label={COPY.aria.volumeSlider}
                    aria-valuemin={0}
                    aria-valuemax={1}
                    aria-valuenow={volume}
                    onMouseDown={onVolumeRailMouseDown}
                  >
                    <VolumeFill style={{ height: `${volume * 100}%` }} />
                    <VolumeThumb
                      style={{ bottom: `calc(${volume * 100}% - 0.4em)` }}
                    />
                  </VolumeRail>
                </VolumePopover>
              )}
            </VolumeWrap>

            {/* Fullscreen is decorative — no `onClick` wire because the
                  preview rect isn't a real video element. `aria-disabled`
                  + `tabIndex={-1}` keep keyboard / AT users from being
                  misled that it's actionable. */}
            <ControlIconBtn
              aria-label={COPY.aria.fullscreen}
              aria-disabled="true"
              tabIndex={-1}
            >
              <FullScreenIconAlt fill="#ffffff" />
            </ControlIconBtn>
          </ControlsInner>
        </ControlsBar>
      )}
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 510×309 reference).
// ---------------------------------------------------------------------------

const Frame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  line-height: 1;
`;

// Caption position container — sized in `em` against the **Frame**'s
// font-size (the dynamic `fontPx` derived from rect dimensions). Keeps the
// caption pinned 40px above the rect bottom (4em × 10px reference) regardless
// of the inner box's own font-size, so the "anchor" stays put even when the
// inner box scales up to 4× for square / vertical previews.
const CaptionAnchor = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4em;
  display: flex;
  justify-content: center;
  pointer-events: none;
  /* Side gutter so the inner caption never butts up against the rect edge. */
  padding: 0 0.4em;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease-out;
`;

// Caption layout container — sets the type scale and centers the text. The
// background lives on the inner `CaptionLines` span (see below) so each
// wrapped line gets its own black rectangle hugging that line, rather than
// one box wrapping the whole multi-line block. Landscape (16:9) renders a
// single thin strip; square (1:1) and vertical (9:16) wrap across multiple
// lines. Vertical is sized to half the square value so the caption doesn't
// dominate the narrower frame.
const CAPTION_FONT_SIZE = {
  landscape: '1.2em',
  square: '3.24em',
  // 10% larger than the prior 2.25em — vertical feed posts get a slightly
  // bigger caption so the type reads as confidently as the square block on
  // narrower frames.
  vertical: '2.475em',
} as const;
const CaptionBox = styled.div<{ $size: 'landscape' | 'square' | 'vertical' }>`
  display: inline-block;
  color: #ffffff;
  font-size: ${({ $size }) => CAPTION_FONT_SIZE[$size]};
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: ${({ $size }) => ($size === 'landscape' ? '1' : '1.35')};
  text-align: center;
  max-width: 100%;
  white-space: ${({ $size }) => ($size === 'landscape' ? 'nowrap' : 'normal')};
`;

// Per-line background. `box-decoration-break: clone` clones the background +
// padding + border-radius onto every line box, so each wrapped line gets its
// own rectangle that hugs the text instead of one box drawn around the whole
// paragraph. Requires `display: inline` on the styled element.
const CaptionLines = styled.span`
  display: inline;
  background: rgba(0, 0, 0, 0.92);
  padding: 0.1em 0.4em;
  border-radius: 0.2em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

// 40px tall gradient strip at the video bottom. Layout/sizing of the inner
// controls lives in `ControlsInner` so the bar's height stays anchored to the
// parent rect while the controls themselves can be scaled independently.
const ControlsBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.6) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  color: #ffffff;
`;

// Inner row that owns the controls layout. Bumping `font-size` here scales
// every child (icons, text, pill, scrubber thumb — all sized in `em`) without
// affecting the bar's outer height.
const ControlsInner = styled.div`
  height: 100%;
  padding: 0 0.9em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  font-size: 1.2em;
`;

// Dim by default; brighten on cursor hover (driven by JS, see component
// effect — chrome is `pointer-events: none` so CSS `:hover` cannot fire).
// Re-enables `pointer-events: auto` on itself so clicks toggle real
// play/pause; the rest of the overlay stays click-through.
const PlayBtn = styled.button<{ $hovered: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.8em;
  height: 1.8em;
  color: #ffffff;
  flex: 0 0 auto;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  opacity: ${({ $hovered }) => ($hovered ? 1 : 0.65)};
  transition: opacity 150ms ease-out;

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

// Mirrors the LinkedIn "back to start" affordance — a leftward-pointing
// triangle with a vertical bar on the left. We re-use `ForwardToEndIcon`
// flipped on the X axis so we don't carry a duplicate SVG.
const BackToStartBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6em;
  height: 1.6em;
  color: #ffffff;
  flex: 0 0 auto;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  opacity: 0.85;
  transition: opacity 150ms ease-out;

  &:hover {
    opacity: 1;
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const BackToStartGlyph = styled.span`
  display: inline-flex;
  /* Flip the "skip-to-end" icon so it points back to the start. */
  transform: scaleX(-1);
`;

// No fixed width/height — children are sized in em so the wrapper grows
// with whatever icon dimensions the call site passes (e.g. the speaker icon
// is rendered larger than the fullscreen icon by design).
const ControlIconBtn = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1em;
  color: #ffffff;
  flex: 0 0 auto;
`;

// Re-enables pointer events when there's a real playback bridge to drive,
// so clicks land for seeking. Track height bumps to give a fatter hit target
// without changing the visual rail thickness (the inner fill keeps the line
// looking the same height as before).
const Scrubber = styled.div<{ $interactive: boolean }>`
  flex: 1;
  height: 0.25em;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 0.2em;
  position: relative;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ScrubberFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 0.2em;

  &::after {
    content: '';
    position: absolute;
    right: -0.45em;
    top: 50%;
    transform: translateY(-50%);
    width: 0.85em;
    height: 0.85em;
    background: #ffffff;
    border-radius: 50%;
  }
`;

const Time = styled.span`
  font-size: 1.125em;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
`;

const SpeedPill = styled.span`
  font-size: 1.0625em;
  font-weight: 600;
  line-height: 1;
`;

// CC button — re-enables pointer events so the click can land while the
// rest of the overlay stays click-through.
const CCButton = styled.button<{ $active: boolean }>`
  pointer-events: auto;
  font-size: 0.85em;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.2em 0.5em;
  border: 1px solid #ffffff;
  border-radius: 0.3em;
  line-height: 1;
  background: ${({ $active }) => ($active ? '#ffffff' : 'transparent')};
  color: ${({ $active }) => ($active ? '#000000' : '#ffffff')};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: ${({ $active }) =>
      $active ? '#ffffff' : 'rgba(255, 255, 255, 0.18)'};
  }
`;

// ----- Volume popover ------------------------------------------------------
// Anchored above the speaker icon, mirroring LinkedIn's hover-popover. Today
// the slider's value is local-only — the renderer doesn't expose a volume
// API, so dragging the thumb adjusts the visualization but doesn't change
// audio. The structure already matches a future bridge wiring (replace the
// local `volume`/`setVolume` with the bridge call).

const VolumeWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
`;

const VolumeBtn = styled.button<{ $muted: boolean }>`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: #ffffff;
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  transition: opacity 150ms ease-out;
`;

const VolumePopover = styled.div`
  position: absolute;
  /* Sit clear of the controls bar with a small gap so the cursor can travel
     between speaker and popover without dropping off either rect. */
  bottom: calc(100% + 0.6em);
  left: 50%;
  transform: translateX(-50%);
  width: 1.8em;
  height: 8em;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 1em;
  padding: 0.7em 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: auto;
`;

const VolumeRail = styled.div`
  flex: 1;
  width: 0.18em;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 0.1em;
  position: relative;
  cursor: pointer;
`;

const VolumeFill = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 0.1em;
`;

const VolumeThumb = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 0.8em;
  height: 0.8em;
  background: #ffffff;
  border-radius: 50%;
`;
